import { PRIORITY_LABELS, formatMoney, isHttpUrl, priceDrop, type Item } from '@closetly/core';
import { storedImageUrl } from '../lib/image';
import { BookmarkIcon } from './Icons';

interface Props {
  item: Item;
  /** Names of the outfits this piece is in. */
  outfits?: string[];
  onOpen: (item: Item) => void;
  onToggleBought: (item: Item) => void;
  onSave: (item: Item) => void;
}

/** Created on this device but not confirmed by the server yet. */
export const isPending = (item: Item) => !!item.pending;

export function ItemCard({ item: i, outfits = [], onOpen, onToggleBought, onSave }: Props) {
  const pending = isPending(i);
  const fmt = (c: number) => formatMoney(c, i.currency || undefined);
  const drop = i.bought ? null : priceDrop(i);
  const watching = i.price_watch && !i.bought;
  const meta = [
    drop && `−${drop.percent} % statt ${fmt(drop.beforeCents)}`,
    watching && i.sold_out && 'Ausverkauft',
    watching && (i.target_price_cents ? `Preisalarm bei ${fmt(i.target_price_cents)}` : 'Preisalarm'),
    i.size && `Gr. ${i.size}`,
    i.color,
    outfits.length > 0 && `Outfit: ${outfits[0]}${outfits.length > 1 ? ` +${outfits.length - 1}` : ''}`,
  ].filter(Boolean) as string[];
  const open = () => !pending && onOpen(i);
  const img = storedImageUrl(i);

  return (
    <div className="tagwrap">
      <article
        className={`tag${i.bought ? ' bought' : ''}${pending ? ' pending' : ''}${img ? ' has-img' : ''}`}
        tabIndex={0}
        aria-label={`${i.name} bearbeiten`}
        aria-busy={pending}
        onClick={open}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            open();
          }
        }}
      >
        <div className="tag-top">
          <span className={`prio p${i.priority}`}>{PRIORITY_LABELS[i.priority]}</span>
          <span>{i.category}</span>
        </div>
        {img && <img className="thumb" src={img} alt="" loading="lazy" />}
        <div>
          <h3>{i.name}</h3>
          {i.shop && <p className="shop">{i.shop}</p>}
        </div>
        <div className={`price${drop ? ' drop' : ''}`}>{i.price_cents ? fmt(i.price_cents) : '–'}</div>
        {meta.length > 0 && (
          <div className="meta">
            {meta.map((m, n) => (
              <span key={m} className={drop && n === 0 ? 'hot' : undefined}>
                {m}
              </span>
            ))}
          </div>
        )}
        {i.note && <p className="note">{i.note}</p>}
        <div className="actions">
          {i.link && isHttpUrl(i.link) && (
            <a
              className="btn"
              href={i.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              Zum Shop ↗
            </a>
          )}
          <button
            type="button"
            className={`btn${i.bought ? '' : ' primary'}`}
            disabled={pending}
            onClick={(e) => {
              e.stopPropagation();
              onToggleBought(i);
            }}
          >
            {i.bought ? 'Zurück auf die Liste' : 'Gekauft'}
          </button>
          <button
            type="button"
            className="btn icon-btn"
            aria-label={`${i.name} merken`}
            disabled={pending}
            onClick={(e) => {
              e.stopPropagation();
              onSave(i);
            }}
          >
            <BookmarkIcon /> Merken
          </button>
        </div>
      </article>
    </div>
  );
}
